import { inject } from '@angular/core';
import { CanActivateChildFn, CanActivateFn, Router } from '@angular/router';
import {
  AuthService,
  MaintenancePermissionSection,
  PermissionAction,
} from '../services/auth.service';

export const rootRedirectGuard: CanActivateFn = () => {
  const auth = inject(AuthService);
  const router = inject(Router);

  if (!auth.isAuthenticated()) {
    return router.createUrlTree(['/login']);
  }

  auth.recordActivity();
  return router.parseUrl(auth.defaultRoute());
};

export const pageContentGuard: CanActivateChildFn = (route, state) => {
  let leaf = route;
  while (leaf.firstChild) {
    leaf = leaf.firstChild;
  }

  // A lazy section can match its bare URL without matching any page inside it.
  // Lazy components are loaded after guards, so check the loader as well.
  if (leaf.component || leaf.routeConfig?.loadComponent) {
    return true;
  }

  return rootRedirectGuard(route, state);
};

export const authGuard: CanActivateFn = (_route, state) => {
  const auth = inject(AuthService);
  const router = inject(Router);

  if (auth.isAuthenticated()) {
    auth.recordActivity();
    return true;
  }

  if (state.url === '/') {
    return router.createUrlTree(['/login']);
  }

  return router.createUrlTree(['/login'], { queryParams: { returnUrl: state.url } });
};

export const loginGuard: CanActivateFn = () => {
  const auth = inject(AuthService);
  const router = inject(Router);

  if (!auth.isAuthenticated()) {
    return true;
  }

  return router.parseUrl(auth.defaultRoute());
};

export const permissionGuard: CanActivateFn = (route, state) => {
  const auth = inject(AuthService);
  const router = inject(Router);
  const action = (route.data?.['permissionAction'] ?? 'read') as PermissionAction;
  const permission = resolvePermission(
    route.data?.['sectionId'],
    route.data?.['maintenanceSection'],
    route.paramMap.get('section'),
    action,
  );

  if (!auth.isAuthenticated()) {
    if (state.url === '/') {
      return router.createUrlTree(['/login']);
    }

    return router.createUrlTree(['/login'], { queryParams: { returnUrl: state.url } });
  }

  if (!auth.canOpenRouteForLaunchPhase(state.url)) {
    const defaultRoute = auth.defaultRoute();

    if (samePath(state.url, defaultRoute)) {
      return false;
    }

    return router.parseUrl(defaultRoute);
  }

  if (
    !permission ||
    (permission.type === 'maintenance'
      ? auth.hasMaintenanceSectionPermission(permission.section, permission.action)
      : auth.hasSectionPermission(permission.sectionId, permission.action))
  ) {
    auth.recordActivity();
    return true;
  }

  const defaultRoute = auth.defaultRoute();

  if (samePath(state.url, defaultRoute)) {
    return false;
  }

  return router.parseUrl(defaultRoute);
};

export const adminToolsGuard: CanActivateFn = (_route, state) => {
  const auth = inject(AuthService);
  const router = inject(Router);

  if (!auth.isAuthenticated()) {
    return router.createUrlTree(['/login'], { queryParams: { returnUrl: state.url } });
  }

  if (auth.canUseAdminTool(state.url)) {
    if (
      samePath(state.url, '/admin-tools') &&
      !auth.canUseAdminTool('/admin-tools/product-labels')
    ) {
      return router.parseUrl('/admin-tools/users');
    }

    auth.recordActivity();
    return true;
  }

  const defaultRoute = auth.defaultRoute();

  if (samePath(state.url, defaultRoute)) {
    return false;
  }

  return router.parseUrl(defaultRoute);
};

function samePath(left: string, right: string) {
  return stripUrlExtras(left) === stripUrlExtras(right);
}

function stripUrlExtras(url: string) {
  return url.split('?')[0].split('#')[0];
}

function resolvePermission(
  sectionId: unknown,
  maintenanceSection: unknown,
  routeSection: string | null,
  action: PermissionAction,
) {
  if (sectionId === 'maintenance') {
    return {
      type: 'maintenance' as const,
      section: (typeof maintenanceSection === 'string'
        ? maintenanceSection
        : routeSection) as MaintenancePermissionSection | string | null,
      action,
    };
  }

  return typeof sectionId === 'number' ? { type: 'section' as const, sectionId, action } : null;
}
